import type { Coord } from "@dailywars/core/engine";
import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";

import { syncCanvasBackingStore } from "#/game/canvasBacking";
import { getAnimatedPosition, type CanvasBoardAnimation, TILE_SIZE } from "#/game/canvasRenderer";

import { useGameCanvasBoardDisplay } from "./boardCanvasDisplay";
import { useCanvasDrawLoop } from "./boardDrawLoop";
import { scrollTileIntoView, type ScrollChromeRefs } from "./boardViewport";
import type { GameCanvasHandle, GameCanvasProps } from "./types";

function useResizeCanvasElement({
  canvasRef,
  width,
  height,
}: {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  width: number;
  height: number;
}): void {
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    syncCanvasBackingStore({
      canvas,
      logicalWidth: width * TILE_SIZE,
      logicalHeight: height * TILE_SIZE,
    });
  }, [canvasRef, height, width]);
}

function useScrollTileIntoView({
  viewportRef,
  canvasRef,
  scrollFocus,
  animationScrollKey,
  displayScale,
  scrollChrome,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  scrollFocus: Coord | null | undefined;
  animationScrollKey: string | null;
  displayScale: number;
  scrollChrome?: ScrollChromeRefs;
}): void {
  const focusKey =
    scrollFocus === null || scrollFocus === undefined
      ? ""
      : `${scrollFocus.x},${scrollFocus.y}:${animationScrollKey ?? ""}`;
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas || !scrollFocus) return;
    scrollTileIntoView({
      viewport,
      canvas,
      focus: scrollFocus,
      displayScale,
      scrollChrome,
    });
  }, [
    animationScrollKey,
    canvasRef,
    displayScale,
    focusKey,
    scrollChrome,
    scrollFocus,
    viewportRef,
  ]);
}

function useFollowMoveAnimation({
  viewportRef,
  canvasRef,
  animation,
  displayScale,
  scrollChrome,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  animation: CanvasBoardAnimation | null;
  displayScale: number;
  scrollChrome?: ScrollChromeRefs;
}): void {
  useEffect(() => {
    if (animation?.type !== "move") return;
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return;
    let frameId = 0;
    let lastKey = "";
    const follow = () => {
      const pos = getAnimatedPosition(animation, performance.now());
      const focus = { x: Math.round(pos.x), y: Math.round(pos.y) };
      const key = `${focus.x},${focus.y}`;
      if (key !== lastKey) {
        lastKey = key;
        scrollTileIntoView({ viewport, canvas, focus, displayScale, scrollChrome });
      }
      frameId = window.requestAnimationFrame(follow);
    };
    frameId = window.requestAnimationFrame(follow);
    return () => window.cancelAnimationFrame(frameId);
  }, [animation, viewportRef, canvasRef, displayScale, scrollChrome]);
}

function useGameCanvasScrollBehavior({
  viewportRef,
  canvasRef,
  scrollFocus,
  animationScrollKey,
  animation,
  displayScale,
  scrollChrome,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  scrollFocus: Coord | null | undefined;
  animationScrollKey: string | null;
  animation: CanvasBoardAnimation | null;
  displayScale: number;
  scrollChrome?: ScrollChromeRefs;
}): void {
  useScrollTileIntoView({
    viewportRef,
    canvasRef,
    scrollFocus,
    animationScrollKey,
    displayScale,
    scrollChrome,
  });
  useFollowMoveAnimation({ viewportRef, canvasRef, animation, displayScale, scrollChrome });
}

function useGameCanvasCoreSetup({
  canvasRef,
  state,
  viewState,
  animation,
  onAnimationComplete,
}: {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  state: GameCanvasProps["state"];
  viewState: GameCanvasProps["viewState"];
  animation: GameCanvasProps["animation"];
  onAnimationComplete: GameCanvasProps["onAnimationComplete"];
}): GameCanvasHandle["setHoveredTileKey"] {
  useResizeCanvasElement({ canvasRef, width: state.width, height: state.height });
  return useCanvasDrawLoop({
    canvasRef,
    state,
    viewState,
    animation,
    onAnimationComplete,
  });
}

export function useGameCanvasRefsAndMetrics(props: GameCanvasProps) {
  const {
    state,
    viewState,
    animation,
    onAnimationComplete,
    onTileHover,
    onTileSelect,
    scrollFocus,
    scrollChrome,
  } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const animationScrollKey = animation ? `${animation.type}:${animation.startedAtMs}` : null;
  const setHoveredTileKey = useGameCanvasCoreSetup({
    canvasRef,
    state,
    viewState,
    animation,
    onAnimationComplete,
  });
  const { displayScale, cssWidth, cssHeight, stickInspectOnClick } = useGameCanvasBoardDisplay({
    viewportRef,
    canvasRef,
    state,
    onTileHover,
    onTileSelect,
  });
  useGameCanvasScrollBehavior({
    viewportRef,
    canvasRef,
    scrollFocus,
    animationScrollKey,
    animation,
    displayScale,
    scrollChrome,
  });
  return {
    canvasRef,
    viewportRef,
    displayScale,
    cssWidth,
    cssHeight,
    stickInspectOnClick,
    setHoveredTileKey,
  };
}
